import { DestroyRef, inject, Injectable } from '@angular/core';
import { CampaignAction, CampaignsService } from '../../services/campaigns.service';
import { AlarisEditPanelService, RowActionSimple } from '@campaign-portal/components-library';
import { exist } from '@campaign-portal/namespace/common/id';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CampaignWizardService } from '../../campaign-wizard/campaign-wizard.service';
import { CampaignUtilsService } from '@shared/services/campaign-utils.service';
import { CampaignEntity } from '@shared/types';

@Injectable()
export class CampaignDetailsActionsService {
	readonly repeat: RowActionSimple<CampaignEntity<exist>> = {
		icon: 'icon-arrow-move-list',
		dynamicLabel: (campaign): string => {
			const channels = campaign?.getTrafficTypes() ?? [];
			const isCampaignPreModerated = channels.some((channel) => this.campaignUtils.isPreModeratedChannel(channel));
			return isCampaignPreModerated ? 'actions.sendRequest' : 'actions.repeat';
		},
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.editPanel.close();
			this.cw.repeat(campaign);
		},
		disabled: (campaign): boolean => !campaign?.canRepeat(),
	};
	private readonly destroyRef = inject(DestroyRef);
	readonly start: RowActionSimple<CampaignEntity<exist>> = {
		icon: 'icon-play',
		label: 'actions.start',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.update({
					...campaign,
					info: {
						...campaign.info,
						scheduled: new Date().toISOString(),
					},
				})
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.editPanel.close());
		},
		shown: (campaign): boolean => !campaign?.isPaused(),
		disabled: (campaign): boolean => !campaign?.canStart(),
	};
	readonly resume: RowActionSimple<CampaignEntity<exist>> = {
		icon: 'icon-play',
		label: 'actions.resume',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.action(CampaignAction.RESUME, campaign)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.editPanel.close());
		},
		shown: (campaign): boolean => campaign?.canResume() ?? false,
	};
	readonly pause: RowActionSimple<CampaignEntity<exist>> = {
		icon: 'icon-pause-circle',
		label: 'actions.pause',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.action(CampaignAction.PAUSE, campaign)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.editPanel.close());
		},
		disabled: (campaign): boolean => !campaign?.canPause(),
	};
	readonly cancel: RowActionSimple<CampaignEntity<exist>> = {
		icon: 'deprecated/stop',
		label: 'actions.cancel',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.action(CampaignAction.CANCEL, campaign)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.editPanel.close());
		},
		disabled: (campaign): boolean => !campaign?.canCancel(),
	};
	readonly delete: RowActionSimple<CampaignEntity<exist>> = {
		icon: '',
		label: 'actions.delete',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.openDialog('Delete', campaign)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.editPanel.close());
		},
		disabled: (campaign): boolean => !campaign?.canDelete(),
		highlight: true,
		separator: true,
	};

	constructor(
		private readonly campaignsService: CampaignsService,
		private readonly editPanel: AlarisEditPanelService,
		private readonly cw: CampaignWizardService,
		private readonly campaignUtils: CampaignUtilsService
	) {}
}
